import { DEFAULT_DATE_MASK, DEFAULT_PART_DATE_MASK } from './estimate';
/**
 * Renders a date against a mask, verbatim. Tokens: YYYY, YY, MMM, MM, DD.
 *
 * **No fallback of any kind.** An empty mask renders an empty string, because
 * that is what the tracking-code generator does and there is no other way to
 * express "this part contributes nothing". The two wrappers below add the
 * fallback appropriate to their setting; this function must not, or the choice
 * stops being visible at the call site.
 *
 * Substitution order is load-bearing: the longer token of each pair has to go
 * first, or `MMMYY` loses its leading `MM` to the month number and reads
 * `07M26` instead of `Jul26`. That is not hypothetical — `MMM` is a documented
 * token on a field's format, and the abbreviation planner shortens `YYYYMM`
 * into it, so masks of exactly that shape reach here on the minting path.
 *
 * `toLocaleString` for the short month means the output depends on the runtime's
 * ICU data. Both the API and the browser have always called it the same way, so
 * sharing this changes nothing — but it is the one part of this package that is
 * not self-contained, and a future runtime change moves both callers together.
 */
export function renderDateMask(date, mask) {
    const yyyy = String(date.getFullYear());
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const mmm = date.toLocaleString('en-US', { month: 'short' });
    return mask
        .replace(/YYYY/g, yyyy)
        .replace(/MMM/g, mmm)
        .replace(/MM/g, mm)
        .replace(/DD/g, dd)
        .replace(/YY/g, yyyy.slice(-2));
}
/**
 * A date PART of a tracking code, as the generator renders it.
 *
 * `??`, so UNSET means `YYYY` and EMPTY means nothing. The distinction is the
 * whole reason this is separate from `formatFieldDate`: a part whose mask was
 * cleared contributes no characters to the minted code, and a fallback that
 * treats blank as unset would silently add four.
 */
export function formatPartDate(date, mask) {
    return renderDateMask(date, mask ?? DEFAULT_PART_DATE_MASK);
}
/**
 * A date FIELD's mask, as the date picker writes it.
 *
 * `||`, so unset AND blank both mean `YYYYMMDD`. The picker has no way to render
 * nothing — it always writes a date — so blank here is an absent preference
 * rather than an instruction to emit an empty string.
 */
export function formatFieldDate(date, mask) {
    return renderDateMask(date, mask || DEFAULT_DATE_MASK);
}
